/** Periodos para la imagen de Finanzas. Las fechas son AAAA-MM-DD del día de la iglesia (lo da la API). */

const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
const toIso = (d: Date) => d.toISOString().slice(0, 10);

export function addDays(iso: string, days: number): string {
  const d = toDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/** Lunes de la semana de la fecha (la semana va de lunes a domingo). */
export function weekStart(iso: string): string {
  const dow = (toDate(iso).getUTCDay() + 6) % 7;
  return addDays(iso, -dow);
}

export function shiftMonth(iso: string, delta: number): string {
  const [y, m] = iso.split('-').map(Number);
  return toIso(new Date(Date.UTC(y!, m! - 1 + delta, 1)));
}

export function monthEnd(iso: string): string {
  const d = toDate(shiftMonth(iso, 1));
  d.setUTCDate(0);
  return toIso(d);
}

export type Preset = { key: string; label: string; from: string; to: string };

export function presets(today: string): Preset[] {
  const week = weekStart(today);
  const lastMonth = shiftMonth(today, -1);
  return [
    { key: 'semana', label: 'Esta semana', from: week, to: addDays(week, 6) },
    { key: 'semana-pasada', label: 'Semana pasada', from: addDays(week, -7), to: addDays(week, -1) },
    { key: 'mes', label: 'Este mes', from: `${today.slice(0, 7)}-01`, to: monthEnd(today) },
    { key: 'mes-pasado', label: 'Mes pasado', from: lastMonth, to: monthEnd(lastMonth) },
  ];
}

const parts = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const month = new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('es-MX', { month: 'long', timeZone: 'UTC' });
  return { y: y!, d: d!, month };
};

/**
 * El periodo en palabras: «Semana del 20 al 26 de octubre de 2026», «Octubre de 2026»,
 * «Del 3 al 15 de octubre de 2026» o, si cruza meses o años, con cada fecha completa.
 */
export function periodLabel(from: string, to: string): string {
  const a = parts(from);
  const b = parts(to);
  const full = (p: ReturnType<typeof parts>) => `${p.d} de ${p.month} de ${p.y}`;
  if (from === to) return full(a);
  if (a.d === 1 && from.slice(0, 7) === to.slice(0, 7) && to === monthEnd(from)) return `${a.month[0]!.toUpperCase()}${a.month.slice(1)} de ${a.y}`;
  const isWeek = weekStart(from) === from && to === addDays(from, 6);
  const range =
    from.slice(0, 7) === to.slice(0, 7)
      ? `del ${a.d} al ${full(b)}`
      : a.y === b.y
        ? `del ${a.d} de ${a.month} al ${full(b)}`
        : `del ${full(a)} al ${full(b)}`;
  return isWeek ? `Semana ${range}` : `D${range.slice(1)}`;
}
